import { Badge, Flex, Textarea } from "@mantine/core";
import { some } from "lodash";
import { DataTableColumn } from "mantine-datatable";
import { useCallback, useMemo } from "react";

import ActionIcon from "../../../components/ActionIcon/ActionIcon.tsx";
import { DatabaseMetadataPopover } from "../../../components/DatabaseMetadataPopover/DatabaseMetadataPopover.tsx";
import SvgDelete from "../../../components/Icons/Delete.tsx";
import SvgEdit from "../../../components/Icons/Edit.tsx";
import SvgInfo from "../../../components/Icons/Info.tsx";
import Spoiler from "../../../components/Spoiler/Spoiler.tsx";
import { TableData, TableRow } from "../../../components/Table/types.ts";
import {
  PaymasterScore,
  UpdatePaymasterNotesInput,
  UserRole,
} from "../../../graphql/generated.ts";
import { useCurrentUserContext } from "../../../providers/CurrentUserProvider.tsx";
import { Paymaster } from "../../../types/paymaster.ts";
import { toHumanReadable } from "../../../utils/string.ts";

interface Props {
  data: Paymaster[] | null | undefined;
  onInlineEdit: (id: number, input: UpdatePaymasterNotesInput) => void;
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
  onOpenCustomers: (id: number) => void;
  onOpenInvoices: (id: number) => void;
}

export const usePaymasterTableData = ({
  data,
  onInlineEdit,
  onEdit,
  onDelete,
  onOpenCustomers,
  onOpenInvoices,
}: Props): TableData => {
  const { user } = useCurrentUserContext();

  const isAdmin =
    user && [UserRole.Admin, UserRole.SuperAdmin].includes(user.role);
  const isRMonTeam =
    user && [UserRole.RmonManager, UserRole.RmonOfficer].includes(user.role);

  const columns: DataTableColumn<TableRow>[] = [
    {
      accessor: "id",
      hidden: true,
    },
    {
      accessor: "name",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Paymaster",
            fieldName: "name",
          }}
        >
          Paymaster Name
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: 300,
    },
    {
      accessor: "highestDpd",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Paymaster",
            fieldName: "highestDpd",
          }}
        >
          Highest DPD
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "lowestDpd",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Paymaster",
            fieldName: "lowestDpd",
          }}
        >
          Lowest DPD
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "score",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Paymaster",
            fieldName: "score",
          }}
        >
          Score
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "ccNotes",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Paymaster",
            fieldName: "ccNotes",
          }}
        >
          Credit Control Remarks
        </DatabaseMetadataPopover>
      ),
    },
    {
      accessor: "rMonNotes",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Paymaster",
            fieldName: "rMonNotes",
          }}
        >
          RMon Remarks
        </DatabaseMetadataPopover>
      ),
    },
    {
      accessor: "customers",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Paymaster",
            fieldName: "customerPaymasters",
          }}
        >
          Customer Details
        </DatabaseMetadataPopover>
      ),
    },
    {
      accessor: "invoices",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Paymaster",
            fieldName: "invoices",
          }}
        >
          Invoice Details
        </DatabaseMetadataPopover>
      ),
    },
    {
      accessor: "actions",
      title: "",
      hidden: !(isAdmin || isRMonTeam),
    },
  ];

  const handleInlineEdit = useCallback(
    (id: number, input: UpdatePaymasterNotesInput) => {
      onInlineEdit(id, input);
    },
    [onInlineEdit]
  );

  const renderRMonNotes = useCallback(
    (item: Paymaster) => {
      const canEdit = isAdmin || isRMonTeam;
      if (canEdit) {
        return (
          <Textarea
            placeholder="Enter Notes"
            autosize
            size="sm"
            defaultValue={item.rMonNotes ?? undefined}
            onBlur={(event) =>
              handleInlineEdit(item.id, {
                rMonNotes: event.target.value,
              })
            }
          />
        );
      }
      return (
        item.rMonNotes && (
          <Spoiler maxStringLength={500} text={item.rMonNotes} />
        )
      );
    },
    [handleInlineEdit, isAdmin, isRMonTeam]
  );

  const renderCCNotes = useCallback(
    (item: Paymaster) => {
      const canEdit = isAdmin || isRMonTeam;
      if (canEdit) {
        return (
          <Textarea
            placeholder="Enter Notes"
            autosize
            size="sm"
            defaultValue={item.ccNotes ?? undefined}
            onBlur={(event) =>
              handleInlineEdit(item.id, {
                ccNotes: event.target.value,
              })
            }
          />
        );
      }
      return (
        item.ccNotes && <Spoiler maxStringLength={500} text={item.ccNotes} />
      );
    },
    [handleInlineEdit, isAdmin, isRMonTeam]
  );

  const renderScore = useCallback((item: Paymaster) => {
    let color;
    switch (item.score) {
      case PaymasterScore.Black:
        color = "dark";
        break;
      case PaymasterScore.Green:
        color = "green";
        break;
      case PaymasterScore.Orange:
        color = "orange";
        break;
      case PaymasterScore.Red:
        color = "red";
        break;
      case PaymasterScore.Yellow:
        color = "yellow";
        break;
    }
    return (
      color && (
        <Badge color={color} size="sm" radius="xs">
          {toHumanReadable(item.score)}
        </Badge>
      )
    );
  }, []);

  const getRow = useCallback(
    (item: Paymaster): TableRow => {
      const hastCustomersWithoutLimit = some(
        item.customerPaymasters,
        (cp) => !cp.limitDetail
      );
      return {
        id: item.id,
        name: item.name,
        highestDpd: item.highestDpd,
        lowestDpd: item.lowestDpd,
        score: renderScore(item),
        rMonNotes: renderRMonNotes(item),
        ccNotes: renderCCNotes(item),
        customers: (
          <ActionIcon
            variant={hastCustomersWithoutLimit ? "error" : undefined}
            onClick={() => onOpenCustomers(item.id)}
          >
            <SvgInfo />
          </ActionIcon>
        ),
        invoices: (
          <ActionIcon onClick={() => onOpenInvoices(item.id)}>
            <SvgInfo />
          </ActionIcon>
        ),
        actions: (
          <Flex gap={8}>
            <ActionIcon onClick={() => onEdit(item.id)}>
              <SvgEdit />
            </ActionIcon>
            <ActionIcon variant="error" onClick={() => onDelete(item.id)}>
              <SvgDelete />
            </ActionIcon>
          </Flex>
        ),
      };
    },
    [
      onDelete,
      onEdit,
      onOpenCustomers,
      onOpenInvoices,
      renderCCNotes,
      renderRMonNotes,
      renderScore,
    ]
  );

  const rows = useMemo(() => {
    return data?.map(getRow);
  }, [data, getRow]);

  return [columns, rows];
};
